import { useEffect, useState } from "react";
import { X } from "lucide-react";
import clsx from "clsx";
import type { ToastEntry } from "../lib/toast";

type Props = {
  toasts: ToastEntry[];
  onDismiss: (id: string) => void;
};

/** Stack of toast cards in the bottom-right of the viewport. Each toast
 *  animates in (fade + slide up), auto-dismisses after its duration, and
 *  can carry an optional action button (e.g. "undo" for soft-deletes). */
export function Toaster({ toasts, onDismiss }: Props) {
  return (
    <div className="pointer-events-none fixed bottom-6 right-6 z-[80] flex flex-col-reverse gap-2">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
      <style>{`
        @keyframes toast-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .toast-enter { animation: toast-in 220ms cubic-bezier(0.22, 1, 0.36, 1); }
      `}</style>
    </div>
  );
}

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: ToastEntry;
  onDismiss: (id: string) => void;
}) {
  // Local "did the user click the action" guard so the auto-dismiss timer
  // doesn't fire a stale action handler after the toast has been undone.
  const [acted, setActed] = useState(false);

  useEffect(() => {
    if (toast.duration === Infinity) return;
    const id = setTimeout(
      () => onDismiss(toast.id),
      toast.duration ?? 5000
    );
    return () => clearTimeout(id);
  }, [toast.id, toast.duration, onDismiss]);

  return (
    <div
      className={clsx(
        "toast-enter pointer-events-auto flex w-80 items-center gap-3 rounded-lane border border-border bg-surface px-4 py-2.5 shadow-modal"
      )}
    >
      <div
        className={clsx(
          "h-2 w-2 shrink-0 rounded-full",
          toast.kind === "danger"
            ? "bg-blush-200"
            : toast.kind === "success"
            ? "bg-mint-200"
            : "bg-lavender-200"
        )}
      />
      <p className="flex-1 truncate text-sm text-ink">{toast.message}</p>
      {toast.action && !acted && (
        <button
          onClick={() => {
            setActed(true);
            toast.action!.onClick();
            onDismiss(toast.id);
          }}
          className="rounded-md px-2 py-1 text-xs font-medium text-lavender-400 transition-colors hover:bg-lavender-50"
        >
          {toast.action.label}
        </button>
      )}
      <button
        onClick={() => onDismiss(toast.id)}
        className="rounded p-0.5 text-ink-mute transition-colors hover:bg-canvas hover:text-ink"
        aria-label="dismiss"
      >
        <X size={12} />
      </button>
    </div>
  );
}
